<template>
    <div class="data-page">
        <PageHeader title="Saldo de Fornecedores" :subtitle="summaryText">
            <template #actions>
                <Button class="p-button-outlined" label="Atualizar" icon="pi pi-refresh"
                    :loading="state.loading.list" @click="loadBalances" />
            </template>
        </PageHeader>

        <div class="data-card">
            <div class="data-card-toolbar">
                <span class="p-input-icon-left data-search">
                    <i class="pi pi-search" />
                    <InputText type="text" v-model="state.search" placeholder="Buscar por fornecedor ou tipo..." />
                </span>
            </div>

            <DataTable class="data-table" :value="filteredBalances" :loading="state.loading.list" dataKey="vendor_id"
                :autoLayout="true" responsiveLayout="scroll">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ state.search ? 'Nenhum fornecedor encontrado para essa busca.' : 'Nenhum fornecedor cadastrado.' }}</span>
                    </div>
                </template>

                <Column field="name" header="Fornecedor">
                    <template #body="slotProps">
                        <span class="record-name">{{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column field="type" header="Tipo">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.type" class="pill pill-info">{{ slotProps.data.type }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="active" header="Status">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="slotProps.data.active ? 'is-on' : 'is-off'">
                            <span class="pill-dot"></span>
                            {{ slotProps.data.active ? 'Ativo' : 'Inativo' }}
                        </span>
                    </template>
                </Column>
                <Column field="balance" header="Saldo">
                    <template #body="slotProps">
                        <span class="cell-amount" :class="{ 'is-negative': slotProps.data.balance < 0 }">
                            {{ formatCurrency(slotProps.data.balance) }}
                        </span>
                    </template>
                </Column>
                <Column field="last_entry_at" header="Última movimentação">
                    <template #body="slotProps">
                        <span class="cell-date">{{ formatDateOrDash(slotProps.data.last_entry_at) }}</span>
                    </template>
                </Column>
                <Column header="Último ajuste">
                    <template #body="slotProps">
                        <template v-if="slotProps.data.last_adjustment">
                            <span class="cell-date">{{ formatDateOrDash(slotProps.data.last_adjustment.created_at) }}</span>
                            <span class="cell-subtext">{{ slotProps.data.last_adjustment.author || '—' }}</span>
                        </template>
                        <span v-else class="empty-value">Nunca ajustado</span>
                    </template>
                </Column>
                <Column class="col-actions">
                    <template #header>
                        Ações
                    </template>
                    <template #body="slotProps">
                        <div class="flex justify-content-end gap-2">
                            <Button class="p-button-outlined p-button-sm" label="Histórico" icon="pi pi-history"
                                :aria-label="`Histórico de ajustes de ${slotProps.data.name}`"
                                @click="openHistory(slotProps.data)" />
                            <Button class="p-button-sm" label="Ajustar saldo" icon="pi pi-pencil"
                                :aria-label="`Ajustar saldo de ${slotProps.data.name}`"
                                @click="openAdjust(slotProps.data)" />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.adjust.visible" :style="{ width: '34rem' }" modal
            :header="`Ajustar saldo de ${state.adjust.vendor?.name || ''}`" :closable="!state.loading.adjust"
            :closeOnEscape="!state.loading.adjust">

            <!-- sem <form>: Enter no campo não pode confirmar um ajuste cuja diferença o operador não viu -->
            <div v-if="state.adjust.vendor" class="data-detail-body">

                <div class="data-detail-grid">
                    <div class="data-detail-item">
                        <span class="data-detail-label">Saldo no hub</span>
                        <span class="data-detail-value cell-amount"
                            :class="{ 'is-negative': state.adjust.vendor.balance < 0 }">
                            {{ formatCurrency(state.adjust.vendor.balance) }}
                        </span>
                    </div>
                    <div class="data-detail-item">
                        <span class="data-detail-label">Diferença lançada</span>
                        <span class="data-detail-value cell-amount" :class="differenceTone" aria-live="polite">
                            {{ differenceText }}
                        </span>
                    </div>
                </div>

                <div class="form-field">
                    <label for="vendor-balance-real">Saldo real no fornecedor *</label>
                    <!-- o InputNumber só atualiza o v-model no blur: o @input mantém a diferença em dia
                         enquanto o operador digita -->
                    <InputNumber id="vendor-balance-real" v-model="state.adjust.balance" mode="currency"
                        currency="BRL" locale="pt-BR" :min="0" :max="999999999.99" :disabled="state.loading.adjust"
                        placeholder="R$ 0,00" aria-describedby="vendor-balance-real-hint" autofocus
                        @input="state.adjust.balance = $event.value" />
                    <small id="vendor-balance-real-hint" class="data-detail-hint">
                        Informe o saldo que aparece agora no painel do fornecedor. O hub lança a diferença e
                        continua somando os pagamentos a partir dele.
                        <template v-if="difference === 0">Sem diferença: o ajuste só registra a conferência.</template>
                    </small>
                </div>

                <div class="form-field">
                    <label for="vendor-balance-reason">Justificativa *</label>
                    <Textarea id="vendor-balance-reason" v-model="state.adjust.reason" rows="3" :maxlength="255"
                        :disabled="state.loading.adjust"
                        :class="{ 'p-invalid': state.adjust.reason.length > 0 && !isReasonValid }"
                        aria-describedby="vendor-balance-reason-hint"
                        placeholder="Por que o saldo está sendo ajustado?" />
                    <small id="vendor-balance-reason-hint" class="data-detail-hint" :class="{ 'is-warn': !isReasonValid }">
                        Informe pelo menos 10 caracteres ({{ state.adjust.reason.length }}/255). O ajuste fica
                        registrado com o seu usuário.
                    </small>
                </div>

            </div>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" :disabled="state.loading.adjust"
                    @click="state.adjust.visible = false" />
                <Button type="button" label="Confirmar ajuste" icon="pi pi-check" :loading="state.loading.adjust"
                    :disabled="!canConfirmAdjust" @click="confirmAdjust" />
            </template>
        </Dialog>

        <Dialog class="data-dialog" v-model:visible="state.history.visible" :style="{ width: '60rem' }" modal
            :header="`Histórico de ajustes de ${state.history.vendor?.name || ''}`">

            <DataTable class="data-table data-table-flush" :value="state.history.items" lazy paginator
                :rows="state.history.perPage" :totalRecords="state.history.total" :first="state.history.first"
                :loading="state.loading.history" dataKey="id" responsiveLayout="scroll"
                paginatorTemplate="PrevPageLink PageLinks NextPageLink" @page="onHistoryPage">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>Nenhum ajuste feito neste fornecedor.</span>
                    </div>
                </template>

                <Column header="Data">
                    <template #body="slotProps">
                        <span class="cell-date">{{ formatDateOrDash(slotProps.data.created_at) }}</span>
                    </template>
                </Column>
                <Column header="Responsável">
                    <template #body="slotProps">
                        {{ slotProps.data.author || '—' }}
                    </template>
                </Column>
                <Column header="Saldo anterior">
                    <template #body="slotProps">
                        <span class="cell-amount">{{ formatCurrency(slotProps.data.previous_balance) }}</span>
                    </template>
                </Column>
                <Column header="Saldo informado">
                    <template #body="slotProps">
                        <span class="cell-amount">{{ formatCurrency(slotProps.data.balance) }}</span>
                    </template>
                </Column>
                <Column header="Diferença">
                    <template #body="slotProps">
                        <span class="cell-amount" :class="amountTone(slotProps.data.difference)">
                            {{ formatSignedCurrency(slotProps.data.difference) }}
                        </span>
                    </template>
                </Column>
                <Column header="Justificativa">
                    <template #body="slotProps">
                        {{ slotProps.data.reason || '—' }}
                    </template>
                </Column>
            </DataTable>
        </Dialog>
    </div>
</template>

<script>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import moment from 'moment'
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import PageHeader from '../../components/PageHeader.vue'
import services from '../../services'
import useNotificationToast from '../../composables/useNotificationToast'

const REASON_MIN_LENGTH = 10

export default {

    components: {
        Button,
        Column,
        DataTable,
        Dialog,
        InputNumber,
        InputText,
        Textarea,
        PageHeader
    },

    setup() {

        const store = useStore()
        const { success, error } = useNotificationToast()

        const state = reactive({
            balances: [],
            search: '',
            loading: {
                list: false,
                adjust: false,
                history: false
            },
            adjust: {
                visible: false,
                vendor: null,
                balance: null,
                reason: ''
            },
            history: {
                visible: false,
                vendor: null,
                items: [],
                total: 0,
                page: 1,
                perPage: 10,
                first: 0
            }
        })

        const filteredBalances = computed(() => {
            const search = state.search.trim().toLowerCase()
            if (!search) return state.balances

            return state.balances.filter((vendor) =>
                [vendor.name, vendor.type].some((field) => String(field || '').toLowerCase().includes(search)))
        })

        const summaryText = computed(() => {
            const count = state.balances.length

            return `${count} ${count === 1 ? 'fornecedor' : 'fornecedores'} · saldo atualizado a cada pagamento confirmado`
        })

        const isReasonValid = computed(() => state.adjust.reason.trim().length >= REASON_MIN_LENGTH)

        const difference = computed(() => {
            if (!state.adjust.vendor || state.adjust.balance === null || state.adjust.balance === undefined) return null

            return Math.round((state.adjust.balance - state.adjust.vendor.balance) * 100) / 100
        })

        const differenceText = computed(() => difference.value === null ? '—' : formatSignedCurrency(difference.value))

        const differenceTone = computed(() => amountTone(difference.value))

        const canConfirmAdjust = computed(() =>
            !state.loading.adjust && difference.value !== null && isReasonValid.value)

        function errorMessage(e, fallback) {
            const data = e.response?.data
            const message = data?.errors?.[0]?.message || data?.message

            // erro interno do Adonis (ex.: E_VALIDATION_FAILURE) não é mensagem para o operador
            if (!message || /^E_[A-Z_]+/.test(message)) return fallback

            return message.charAt(0).toUpperCase() + message.slice(1)
        }

        async function loadBalances() {
            state.loading.list = true

            try {
                const response = await services.vendorBalances.getAll({ token: store.getters.getToken })
                state.balances = response.data
            } catch (e) {
                error('Não foi possível carregar os saldos.')
            } finally {
                state.loading.list = false
            }
        }

        function openAdjust(vendor) {
            state.adjust = { visible: true, vendor: { ...vendor }, balance: null, reason: '' }
        }

        async function confirmAdjust() {
            if (!canConfirmAdjust.value) return

            state.loading.adjust = true
            const vendor = state.adjust.vendor

            try {
                await services.vendorBalances.adjust({
                    vendorId: vendor.vendor_id,
                    balance: state.adjust.balance,
                    expectedBalance: vendor.balance,
                    reason: state.adjust.reason.trim(),
                    token: store.getters.getToken
                })

                success(`Saldo de ${vendor.name} ajustado.`)
                state.adjust.visible = false
                await loadBalances()
            } catch (e) {
                error(errorMessage(e, 'Não foi possível ajustar o saldo.'))

                // o saldo mudou enquanto o operador digitava: recarrega para ele conferir de novo,
                // mantendo o que já foi digitado
                if (e.response?.status === 409) {
                    await loadBalances()
                    const current = state.balances.find((item) => item.vendor_id === vendor.vendor_id)
                    if (current) state.adjust.vendor = { ...current }
                }
            } finally {
                state.loading.adjust = false
            }
        }

        // trocar de fornecedor ou de página rápido não pode deixar a resposta antiga por cima da nova
        let historyRequest = 0

        async function loadHistory() {
            const request = ++historyRequest
            state.loading.history = true

            try {
                const response = await services.vendorBalances.getAdjustments({
                    vendorId: state.history.vendor.vendor_id,
                    page: state.history.page,
                    perPage: state.history.perPage,
                    token: store.getters.getToken
                })

                if (request !== historyRequest) return

                state.history.items = response.data.data
                state.history.total = response.data.meta.total
            } catch (e) {
                if (request === historyRequest) error('Não foi possível carregar o histórico de ajustes.')
            } finally {
                if (request === historyRequest) state.loading.history = false
            }
        }

        function openHistory(vendor) {
            state.history = { ...state.history, visible: true, vendor, items: [], total: 0, page: 1, first: 0 }
            loadHistory()
        }

        function onHistoryPage(event) {
            state.history.page = event.page + 1
            state.history.first = event.first
            loadHistory()
        }

        function formatCurrency(value) {
            if (value === null || value === undefined) return '—'

            return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }

        function formatSignedCurrency(value) {
            if (value === null || value === undefined) return '—'
            if (Number(value) === 0) return formatCurrency(0)

            return `${value > 0 ? '+' : '−'} ${formatCurrency(Math.abs(value))}`
        }

        function amountTone(value) {
            if (!value) return ''

            return value > 0 ? 'is-positive' : 'is-negative'
        }

        // as datas chegam no horário de Brasília com sufixo Z, como no resto do painel
        function formatDateOrDash(dateString) {
            if (!dateString) return '—'

            const date = moment.utc(dateString)

            return date.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : '—'
        }

        loadBalances()

        return {
            state,
            filteredBalances,
            summaryText,
            isReasonValid,
            difference,
            differenceText,
            differenceTone,
            canConfirmAdjust,
            loadBalances,
            openAdjust,
            confirmAdjust,
            openHistory,
            onHistoryPage,
            formatCurrency,
            formatSignedCurrency,
            amountTone,
            formatDateOrDash
        }
    }
}
</script>
